import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Wchat entry intro — inspired by 21st.dev "Unique Testimonial"
// Smooth fade/blur between quotes, avatar row that expands active into a pill
export default function WchatIntro({ onDone, currentMember, members = [] }) {
  const testimonials = React.useMemo(() => {
    const memberName = currentMember?.name?.split(' ')[0] || 'Sócio';
    const others = (members || []).filter(m => m.id !== currentMember?.id && m.photo).slice(0, 2);
    return [
      {
        id: 'w',
        quote: 'Converse com W, sua IA sommelier.',
        role: 'W — Winners IA',
        author: 'W',
        avatar: null, // stylized W
        isW: true,
      },
      {
        id: 'privacy',
        quote: 'Mensagens privadas, criptografia de ponta.',
        role: 'Wchat — seguro por padrão',
        author: memberName,
        avatar: currentMember?.photo || null,
        initials: (currentMember?.name || 'W').charAt(0).toUpperCase(),
      },
      ...(others.length > 0 ? [{
        id: 'community',
        quote: 'A comunidade WClub em tempo real.',
        role: `${others[0].name.split(' ')[0]} e mais sócios`,
        author: others[0].name.split(' ')[0],
        avatar: others[0].photo,
        initials: others[0].name.charAt(0).toUpperCase(),
      }] : [{
        id: 'community',
        quote: 'A comunidade WClub em tempo real.',
        role: 'Sócios WClub',
        author: 'WClub',
        avatar: null,
        initials: 'W',
      }]),
    ];
  }, [currentMember, members]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayed, setDisplayed] = useState(testimonials[0]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [fadingOut, setFadingOut] = useState(false);

  const handleSelect = (index) => {
    if (index === activeIndex || isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setDisplayed(testimonials[index]);
      setActiveIndex(index);
      setTimeout(() => setIsAnimating(false), 400);
    }, 200);
  };

  // Auto-advance through testimonials, then fade out and call onDone
  useEffect(() => {
    const timers = [];
    testimonials.forEach((_, i) => {
      if (i === 0) return;
      timers.push(setTimeout(() => handleSelect(i), i * 1400));
    });
    // Final fade-out after last quote
    timers.push(setTimeout(() => setFadingOut(true), testimonials.length * 1400 + 300));
    timers.push(setTimeout(() => onDone && onDone(), testimonials.length * 1400 + 900));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Avatar = ({ t, size = 40, ring }) => {
    if (t.isW) {
      return (
        <div
          className="rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            width: size, height: size,
            background: 'linear-gradient(135deg, #1f1f24 0%, #0a0a0c 100%)',
            border: ring ? '2px solid rgba(10,132,255,0.9)' : '1.5px solid rgba(255,255,255,0.12)',
            boxShadow: ring ? '0 0 18px rgba(10,132,255,0.45)' : 'none',
          }}
        >
          <span className="font-['Jost'] font-semibold text-white" style={{ fontSize: size * 0.42 }}>W</span>
        </div>
      );
    }
    return (
      <div
        className="rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
        style={{
          width: size, height: size,
          background: 'linear-gradient(135deg, #3a3a3c 0%, #1c1c1e 100%)',
          border: ring ? '2px solid rgba(10,132,255,0.9)' : '1.5px solid rgba(255,255,255,0.1)',
          boxShadow: ring ? '0 0 18px rgba(10,132,255,0.45)' : 'none',
        }}
      >
        {t.avatar ? (
          <img src={t.avatar} alt={t.author} className="w-full h-full object-cover" />
        ) : (
          <span className="font-['Jost'] font-semibold text-white/90" style={{ fontSize: size * 0.42 }}>{t.initials || 'W'}</span>
        )}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {!fadingOut && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
          className="fixed inset-0 z-[70] flex flex-col items-center justify-center px-8"
          style={{ background: '#000', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
          data-testid="wchat-intro"
          onClick={() => { setFadingOut(true); setTimeout(() => onDone && onDone(), 500); }}
        >
          {/* Subtle background glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 50% 35%, rgba(10,132,255,0.10) 0%, transparent 55%)',
            }}
          />

          {/* Quote Container */}
          <div className="relative z-10 max-w-[360px] text-center">
            <div
              className="font-['Jost'] text-white/20 leading-none mb-2"
              style={{ fontSize: '72px', fontWeight: 300, transform: 'translateY(8px)' }}
            >
              &ldquo;
            </div>

            <AnimatePresence mode="wait">
              <motion.p
                key={displayed.id}
                initial={{ opacity: 0, y: 8, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -8, filter: 'blur(8px)' }}
                transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                className="text-white font-['Jost'] text-[22px] leading-[1.35] font-light tracking-tight"
              >
                {displayed.quote}
              </motion.p>
            </AnimatePresence>

            <div
              className="font-['Jost'] text-white/20 leading-none mt-2"
              style={{ fontSize: '72px', fontWeight: 300, transform: 'translateY(-30px) rotate(180deg)' }}
            >
              &ldquo;
            </div>
          </div>

          {/* Role */}
          <div className="relative z-10 mt-2 mb-6 h-5">
            <AnimatePresence mode="wait">
              <motion.p
                key={displayed.id + '-role'}
                initial={{ opacity: 0, y: 4, filter: 'blur(6px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -4, filter: 'blur(6px)' }}
                transition={{ duration: 0.4 }}
                className="text-white/50 font-['Jost'] text-[12px] tracking-wide"
              >
                {displayed.role}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Avatar row - active expands into pill with name */}
          <div className="relative z-10 flex items-center gap-2">
            {testimonials.map((t, i) => {
              const isActive = i === activeIndex;
              const isHovered = hoveredIndex === i && !isActive;
              const showName = isActive || isHovered;
              return (
                <motion.div
                  key={t.id}
                  onClick={(e) => { e.stopPropagation(); handleSelect(i); }}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  layout
                  className="relative flex items-center cursor-pointer rounded-full overflow-hidden"
                  style={{
                    background: isActive ? 'rgba(255,255,255,0.95)' : 'transparent',
                    boxShadow: isActive ? '0 6px 20px rgba(255,255,255,0.15)' : 'none',
                    padding: showName ? '4px 12px 4px 4px' : '2px',
                    transition: 'background 0.4s cubic-bezier(0.4,0,0.2,1), padding 0.4s cubic-bezier(0.4,0,0.2,1), box-shadow 0.4s',
                  }}
                >
                  <Avatar t={t} size={36} ring={isActive} />
                  <AnimatePresence>
                    {showName && (
                      <motion.span
                        initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                        animate={{ opacity: 1, width: 'auto', marginLeft: 8 }}
                        exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                        className={`font-['Jost'] font-medium text-[12px] whitespace-nowrap ${isActive ? 'text-black' : 'text-white/70'}`}
                      >
                        {t.author}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* Skip hint */}
          <p className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/25 text-[10px] font-['Jost'] tracking-wider">
            Toque para entrar
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
